import React from "react";
import flattenDeep from "lodash/flattenDeep";
import isEmpty from "lodash/isEmpty";
import sortBy from "lodash/sortBy";
import pick from "lodash/pick";
import mapValues from "lodash/mapValues";
import { v4 as uuidv4 } from "uuid";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  faDisease,
  faMicroscope,
  faGlobe,
  faUser,
  faGenderless,
  faWaveSquare,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import {
  pathogenicColor,
  likelyPathogenicColor,
  uncertainSignificanceColor,
  benignColor,
  premiumColor,
} from "../../../../common/helpers/colors";
import LinkifyBase from "../../../../components/social/LinkifyBase";
import {
  getAmpClassifications,
  getAmpVerdict,
  getAmpErrors,
  getAmpVersion,
  getAmpSampleFindings,
  getAcmgAnnotationGene,
  getApprovedTherapies,
  getGeneSymbol,
  getHgvsP1,
  getAMPThrottling,
} from "../../../../state/selectors/AMPComponent.selectors";
import {
  getAcmgCodingImpact,
  getAdditionalDatabases,
} from "../../../../state/selectors/ACMGComponent.selectors";
import {
  getApiUrl,
  getIfSaphetorUser,
  getPreferences,
} from "../../../../state/selectors/general.selectors";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";
import {
  ToggleButton,
  Table,
  ColoredPill,
  Loading,
  SampleFindingsTable,
} from "../../../../components/atoms";
import ApprovedTherapiesTable from "../../../../components/atoms/ApprovedTherapiesTable/ApprovedTherapiesTable";
import ClassificationPill from "../../../../components/atoms/ClassificationPill/ClassificationPill";
import store from "../../../../state/store";
import {
  varsomeClinicalUrl,
  varsomeHost,
  varsomePremiumUrl,
} from "../../../../common/helpers/Urls";
import {
  capitalizeFirstLetter,
  displayVarsomePremiumLogo,
  getVarsoUrl,
  isSomatic,
  userIsPremium,
} from "../../../../common/helpers/helpers";
import SearchModal from "../../../../components/atoms/SearchModal/SearchModal";
import {
  ETHINICITES_ABBREVIATIONS_TO_ETHNICITIES,
  SEX_ABBREVIATIONS_TO_SEX,
} from "../../../../common/helpers/Constant";
import AdditionalDatabasesMessage from "../../../../components/atoms/AdditionalDatabasesMessage";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import HubspotBanner from "../../../../components/hubspot/HubspotBanner";
import ls from "../../../../common/helpers/localStorageActions";
import Modal from "../../../../components/atoms/Modal";
import {
  StyledTierCell,
  StyledErrorWrapper,
  StyledSaphetorErrorMessage,
  StyledGeneralErrorMessage,
  StyledVarsomeClinicalMessage,
  firstColumnStyles,
  secondColumnStyles,
  thirdColumnStyles,
  StyledMissenseText,
} from "./AMP.styles";
import { ampRulesMapping } from "../../../../common/constants";
import GeneWithRefGenomeLink from "../../../../common/helpers/GeneWithRefGenomeLink";
import TermsOfUseAMP from "./TermsOfUseAMP";
import AnnotationThrottled from "../acmg/annotation-throttled";
import { httpRequest } from "../../../../services/http";

// Here we create the headers and rows data model for the table component
class AMP extends React.Component {
  constructor() {
    super();
    this.state = {
      componentName: "AMPComponent",
      rows: [],
      showTierIII: ls.getAmpFullDetail() || false,
      tiersSortOrder: ["Tier I", "Tier II", "Tier III", "Tier IV"],
      tierColors: {
        "Tier I": pathogenicColor,
        "Tier II": likelyPathogenicColor,
        "Tier III": uncertainSignificanceColor,
        "Tier IV": benignColor,
      },
      tiersSelect: [],
      tierSelectOptions: [
        { value: "Tier I", label: "Tier I" },
        { value: "Tier II", label: "Tier II" },
        { value: "Tier III", label: "Tier III" },
        { value: "Tier IV", label: "Tier IV" },
      ],
      tierLabelsMapping: {
        "Tier I": 1,
        "Tier II": 2,
        "Tier III": 3,
        "Tier IV": 4,
      },
      isVerdictLoading: false,
      isSearchModalOpen: false,
      isTermsModalOpen: false,
    };
  }

  componentDidMount() {
    const { ampVerdict } = this.props;
    if (__IS_COMPONENTS_PORTAL__) {
      // clinical mounts and remounts components on table switch and on tab show or hide.
      // since amp has a mixed case of classifications and verdict between state and props at
      // render. this is required
      this.setVerdict(ampVerdict);
      this.setTiers(this.calculateRows);
    } else if (ampVerdict && !__IS_COMPONENTS_PORTAL__) {
      this.setState({
        ampVerdict,
      });
      this.setTiers(this.calculateRows);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { genome, ampClassifications, ampVerdict } = this.props;
    const { showTierIII } = this.state;

    if (
      genome !== prevProps.genome ||
      ampClassifications !== prevProps.ampClassifications
    ) {
      this.setTiers(this.calculateRows);
    }

    if (showTierIII !== prevState.showTierIII) {
      this.calculateRows();
    }

    if (ampVerdict !== prevProps.ampVerdict) {
      this.setVerdict(ampVerdict);
    }
  }

  setVerdict = (ampVerdict) => {
    this.setState({
      ampVerdict,
      isVerdictLoading: false,
    });
  };

  getTierChange = () => {
    const { apiUrl } = this.props;
    const { tiersSelect, tierLabelsMapping } = this.state;
    let rules = "";

    tiersSelect.forEach((t) => {
      rules += `&rules=${t.name}${tierLabelsMapping[t.value]}`;
    });
    const url = `${apiUrl}/amp-verdict?${rules}`;

    this.setState({
      isVerdictLoading: true,
    });
    httpRequest
      .get(url)
      .then((response) => {
        const { data } = response;
        if (data.AMP_rules) {
          this.setVerdict(data.AMP_rules);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  setTiers = (cb) => {
    const { ampClassifications } = this.props;
    if (!ampClassifications) return;

    const tiers = ampClassifications.map((a) => ({
      name: a.name,
      value: a.tier,
      label: a.tier,
    }));

    this.setState(
      {
        tiersSelect: [...tiers],
      },
      () => {
        if (typeof cb === "function") {
          cb();
        }
      }
    );
  };

  onTierChange = (rule, tier) => {
    this.setState(
      (prevState) => ({
        tiersSelect: [
          ...prevState.tiersSelect.map((t) =>
            t.name === rule ? { ...t, value: tier.value, label: tier.label } : t
          ),
        ],
      }),
      () => {
        this.calculateRows();
        this.getTierChange();
      }
    );
  };

  getTier = (tier, type) => {
    const { tierColors } = this.state;

    return (
      <StyledTierCell type={type} color={tierColors[tier]}>
        {tier}
      </StyledTierCell>
    );
  };

  calculateRows = () => {
    const { ampClassifications, ampVerdict } = this.props;
    const { showTierIII, tiersSortOrder, tierSelectOptions, tiersSelect } =
      this.state;

    const rows = [];
    const typeOfTierToShow = "Tier III";

    if (!ampClassifications) return;

    // 1. show the tier III rules if the show all is selected or the verdict is tier III
    const filteredAmpClassifications = ampClassifications
      .filter((f) =>
        showTierIII
          ? f
          : ampVerdict !== typeOfTierToShow && f.tier !== typeOfTierToShow
      )
      // when the show full details is unchecked it show only
      // the first user explain that matches the tier
      .map((s) => {
        return showTierIII
          ? s
          : {
              ...s,
              user_explain: mapValues(pick(s.user_explain, [s.tier]), (v) => {
                // only the first value
                return [v[0]];
              }),
            };
      });

    filteredAmpClassifications.map((c) => {
      const tiersKeys = c.user_explain ? Object.keys(c.user_explain) : [];
      const tierSelection = tiersSelect.filter((t) => t.name === c.name)[0];
      const isTierDisabled = tierSelection.label !== c.tier;

      let sortedTiersKeys = sortBy(tiersKeys, (item) =>
        tiersSortOrder.indexOf(item)
      );

      // show Tier III if:
      // 1. if show all is checked
      // 2. if verdict is Tier III and rule Tier is III
      const shouldShowFullDetails = showTierIII || c.tier === typeOfTierToShow;

      if (!shouldShowFullDetails) {
        sortedTiersKeys = sortedTiersKeys.filter(
          (tier) => tier !== typeOfTierToShow
        );
      }

      const userExplainRows =
        c.user_explain &&
        sortedTiersKeys.map((k) => {
          return c.user_explain[k];
        });

      let keysAndLen = sortedTiersKeys.map((key) => ({
        name: key,
        len: c.user_explain[key].length,
      }));

      // if the original tier rule is not Tier III remove all the Tier III explanations
      if (!shouldShowFullDetails) {
        keysAndLen = keysAndLen.filter((k) => k.name !== typeOfTierToShow);
      }

      const flattenedUserExplainRows = userExplainRows
        ? flattenDeep(userExplainRows)
        : [""];

      // keep the tiers that are added
      const tiersAdded = [];
      const selectedTier = tierSelection.label;

      const userExplainColumn =
        keysAndLen &&
        keysAndLen.length > 0 &&
        flattenedUserExplainRows.map((e, index) => ({
          id: uuidv4(),
          columns: [
            // add the first column of the row in index 0
            ...(index === 0
              ? [
                  {
                    id: uuidv4(),
                    content: (
                      <React.Fragment>
                        <ColoredPill
                          colorParams={{
                            type: "tier",
                            value: selectedTier,
                          }}
                          content={
                            <ClassificationPill
                              name={
                                <div
                                  className="tw-flex tw-items-center tw-text-sm tw-font-bold"
                                  style={{
                                    color: "white",
                                  }}
                                >
                                  {ampRulesMapping[c.name] || c.name}
                                </div>
                              }
                              options={tierSelectOptions}
                              onChange={(selectEvent) => {
                                this.onTierChange(c.name, selectEvent);
                              }}
                              value={tierSelection}
                            />
                          }
                        />
                      </React.Fragment>
                    ),
                    rowSpan: flattenedUserExplainRows.length,
                    styles: firstColumnStyles,
                  },
                ]
              : []),
            // if there is an explain texts add the texts from here along with the tiers
            ...sortedTiersKeys
              .map((k, explainIndex) => {
                const len = c.user_explain && c.user_explain[k].length;
                // this length is taken into account for when to add the next tier along with the rowspan
                const addedTierLengthNumber = keysAndLen
                  .map((l) => (tiersAdded.includes(l.name) ? l.len : 0))
                  .reduce((a, b) => a + b);

                // add first key
                const tierToAdd = {
                  id: uuidv4(),
                  content: this.getTier(k),
                  ...(len && { rowSpan: len }),
                  styles: secondColumnStyles,
                  isDisabled: isTierDisabled,
                };

                // add the first tier in the row
                if (explainIndex === 0 && !tiersAdded.includes(k)) {
                  tiersAdded.push(k);
                  return tierToAdd;
                }
                // all the additional tiers will be added from here depending of the items they hold
                if (
                  index > 0 &&
                  addedTierLengthNumber > 0 &&
                  addedTierLengthNumber === index &&
                  !tiersAdded.includes(k)
                ) {
                  tiersAdded.push(k);
                  return tierToAdd;
                }

                return {};
              })
              // remove all the empty objects returned from the map
              .filter((k) => !isEmpty(k)),
            {
              id: uuidv4(),
              content: <LinkifyBase txt={e} />,
              rowSpan: 1,
              styles: thirdColumnStyles,
              isDisabled: isTierDisabled,
            },
          ],
        }));

      // if empty array is returned from the map ignore it
      if (!isEmpty(userExplainColumn)) {
        rows.push(...userExplainColumn);
      }

      return c;
    });

    this.setState({
      rows,
    });
  };

  getErrors = () => {
    const { ampErrors, isSaphetorUser } = this.props;
    if (!ampErrors || (ampErrors && ampErrors.length === 0)) return null;

    let rulesErrors = "";
    ampErrors.forEach((item, index) => {
      const toConcat = `${index > 0 ? `,` : ""} ${item.classifier}`;
      rulesErrors += toConcat;
    });

    return (
      <StyledErrorWrapper>
        {isSaphetorUser && (
          <StyledSaphetorErrorMessage>
            {ampErrors &&
              ampErrors.map((item) => (
                <div key={uuidv4()} className="tw-text-red-500">
                  {item.classifier && (
                    <b>
                      {item.classifier}
                      {": "}
                    </b>
                  )}
                  {item.exception}
                </div>
              ))}
          </StyledSaphetorErrorMessage>
        )}
        <div>
          <StyledGeneralErrorMessage>
            <i className="fa fa-exclamation-circle" />
            {`There has been an internal error processing the AMP for this variant in the rules: ${
              ampErrors && rulesErrors
            }`}
          </StyledGeneralErrorMessage>
        </div>
      </StyledErrorWrapper>
    );
  };

  getPanelRightButtons = () => {
    const { geneSymbol, hgvsP1 } = this.props;
    return (
      <div className="tw-inline-block tw-float-right">
        <div
          className="tw-inline-block"
          style={{
            marginRight: "1.2rem",
          }}
        >
          {geneSymbol && hgvsP1 && (
            <a
              href={getVarsoUrl(
                `https://www.oncokb.org/gene/${geneSymbol}/${hgvsP1}`
              )}
              className="tw-mr-5"
              target="_blank"
            >
              View on OncoKB
            </a>
          )}
          <a
            onClick={() =>
              this.setState({
                isTermsModalOpen: true,
              })
            }
          >
            Terms of use
          </a>
        </div>
        <span className="tw-mr-5">
          <a
            href={`${varsomeHost}/about/resources/somatic-implementation/`}
            target="_blank"
          >
            Documentation
          </a>
        </span>
      </div>
    );
  };

  getAMPGeneUsedForAnnotation = () => {
    const { acmgAnnotationGene, acmgCodingImpact } = this.props;
    let text = null;

    if (acmgAnnotationGene && acmgCodingImpact) {
      const geneUrl = `${varsomeHost}/gene/${acmgAnnotationGene}`;
      const gene = (
        <GeneWithRefGenomeLink href={geneUrl} target="_blank">
          {acmgAnnotationGene}
        </GeneWithRefGenomeLink>
      );

      text = (
        <StyledMissenseText>
          {`${capitalizeFirstLetter(acmgCodingImpact)} variant in gene `}
          {gene}
        </StyledMissenseText>
      );
    }

    return text;
  };

  displayJaxMessageOnClinical = () => {
    return __IS_COMPONENTS_PORTAL__ ? (
      <div className="varsome-text-muted tw-mt-12">
        <small>
          <i>
            Somatic gene variant annotations and related content have been
            powered by The Jackson Laboratory Clinical Knowledgebase (JAX-CKB™).
          </i>
        </small>
      </div>
    ) : null;
  };

  displayVarsomeClinicalMessageOnVarsome = () => {
    return !__IS_COMPONENTS_PORTAL__ ? (
      <StyledVarsomeClinicalMessage className="tw-pt-6">
        <small>
          <i className="fa fa-exclamation-triangle" />
          {"  Users of"}
          <a href={varsomeClinicalUrl} target="_blank">
            {" VarSome Clinical "}
          </a>
          benefit from additional cancer databases whose licenses prevent us
          from displaying them in VarSome itself.
        </small>
      </StyledVarsomeClinicalMessage>
    ) : null;
  };

  displayAdditionalDatabasesSources = () => {
    const DB_SOURCES = {
      JAX_CKB: "CKB",
      COSMIC: "Cosmic",
    };

    const { additionalDatabases } = this.props;
    const jaxAndCosmic = additionalDatabases
      .filter(
        (e) => e.source === DB_SOURCES.JAX_CKB || e.source === DB_SOURCES.COSMIC
      )
      .map((e) => e.source);
    const uniqueJAXCOSMICSources = Array.from(new Set(jaxAndCosmic));

    const allOtherSources = additionalDatabases
      .filter(
        (e) => e.source !== DB_SOURCES.JAX_CKB && e.source !== DB_SOURCES.COSMIC
      )
      .map((e) => e.source);
    const uniqueOtherSources = Array.from(new Set(allOtherSources));

    if (!uniqueOtherSources.length && !jaxAndCosmic.length) return null;

    const datasourcesString = uniqueOtherSources.join(", ");

    const jaxAndCosmicMessage = uniqueJAXCOSMICSources.length
      ? uniqueJAXCOSMICSources.map((db) => (
          <small>
            <i className="fa fa-exclamation-triangle" />
            <AdditionalDatabasesMessage database={db} />
          </small>
        ))
      : "";

    return (
      <StyledVarsomeClinicalMessage className="tw-pb-10 tw-flex tw-flex-col">
        {datasourcesString && (
          <small>
            <i className="fa fa-exclamation-triangle" />
            {`This variant has been found in additional Premium sources: ${datasourcesString}.`}
          </small>
        )}
        {jaxAndCosmicMessage}
      </StyledVarsomeClinicalMessage>
    );
  };

  searchModalShow = () => {
    this.setState({
      isSearchModalOpen: true,
    });
  };

  searchModalHide = () => {
    this.setState({
      isSearchModalOpen: false,
    });
  };

  render() {
    const {
      isLoading,
      hasNext,
      ampClassifications,
      version,
      ampSampleFindings,
      approvedTherapies,
      ampThrottlingInfo,
      componentTitle,
    } = this.props;

    if (isLoading) return null;

    const {
      componentName,
      rows,
      showTierIII,
      ampVerdict,
      isVerdictLoading,
      isSearchModalOpen,
      isTermsModalOpen,
    } = this.state;
    const { variantQueryMode } = store.getState();
    const {
      age,
      cancer_type: cancerType,
      tissue_type: tissueType,
      ethnicity,
      sex,
      inheritance,
    } = variantQueryMode;
    const ethnicityFromAbbreviation =
      ethnicity && ETHINICITES_ABBREVIATIONS_TO_ETHNICITIES[ethnicity];
    const sexFromAbbreviation = sex && SEX_ABBREVIATIONS_TO_SEX[sex];

    /** VarSome FREE Platform
     *    Case 1: Germline annotation mode ---> Don't display the component.
     *    Case 2: Premium User ---> amp_annotation field doesn't have data ---> Don't display the component.
     *    Case 3: Premium User ---> amp_annotation field has data ---> Display the component with data.
     *    Case 4: Non Premium User ---> somatic && amp_annotation field doesn't have data ---> Display the Hubspot banner.
     *    Case 5: Non Premium User ---> somatic && amp_annotation field has data ---> Display the component with data and Demo flag.
     *            VarSome Clinical Platform
     *    Case 6: amp_annotation field doesn't have data ---> Don't display the component.
     *    Case 7: amp_annotation field has data ---> Display the component with data..
     */
    const isUserPremium = userIsPremium();
    const isClinical = __IS_COMPONENTS_PORTAL__;
    const isGermline = !isSomatic();
    /** Case 6 */
    if (isClinical && !ampVerdict && !ampClassifications && !ampThrottlingInfo)
      return null;
    /** Case 1, Case 2 */
    if (
      !isClinical &&
      (isGermline || (isUserPremium && !ampVerdict && !ampClassifications)) &&
      !ampThrottlingInfo
    )
      return null;

    const headers = [
      { id: 1, title: "Evidence" },
      { id: 2, title: "" },
      {
        id: 3,
        title: (
          <div className="tw-flex tw-items-center tw-justify-end">
            <ToggleButton
              onToggleClick={(e) => {
                this.setState({ showTierIII: e.target.checked });
                ls.setAmpFullDetail(e.target.checked);
              }}
              label="Show full detail"
              isChecked={showTierIII}
              noPaddingAndMargin={true}
            />
          </div>
        ),
      },
    ];

    let message = "";
    if (!__IS_COMPONENTS_PORTAL__) {
      message = <span>{" - Educational use only"}</span>;
    }

    const url = getVarsoUrl(
      "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5707196/"
    );

    if (ampThrottlingInfo) {
      return (
        <AnnotationThrottled
          informativeMessage={message}
          version={version}
          url={url}
          varsomeHost={varsomeHost}
          componentName={componentName}
          isACMG={false}
          componentTitle={componentTitle}
        />
      );
    }
    const { tiersSelect } = this.state;
    return !hasNext && ampClassifications && ampVerdict ? (
      <React.Fragment>
        <div className="varsome-result-fixed">
          <div className="varsome-container varsome-container__basic">
            <ComponentPanel
              version={version}
              informativeMessage={message}
              titleDetailsRight={this.getPanelRightButtons()}
              premiumLogo={displayVarsomePremiumLogo()}
              content={
                <React.Fragment>
                  {ampClassifications &&
                  ampVerdict &&
                  this.getErrors() === null ? (
                    <div className="varsome-inner-container">
                      <div className="tw-flex tw-flex-col tw-pb-4 tw-font-semibold">
                        <div className="tw-flex">
                          <div className="tw-flex tw-max-w-2/3 tw-flex-grow tw-justify-start tw-flex-col tw-items-center">
                            {!isUserPremium && !isClinical && (
                              <div className="tw-flex tw-justify-center tw-pb-10">
                                <div
                                  className="tw-flex-inline tw-justify-center tw-py-1 tw-px-2 tw-mr-2"
                                  style={{
                                    color: premiumColor,
                                    border: `1px solid ${premiumColor}`,
                                    maxWidth: "610px",
                                  }}
                                >
                                  This is a preview of the Somatic Variant
                                  Classification module featured in{" "}
                                  <a href={varsomePremiumUrl} target="_blank">
                                    VarSome Premium
                                  </a>{" "}
                                </div>
                              </div>
                            )}
                            {!isUserPremium &&
                              !isClinical &&
                              this.displayAdditionalDatabasesSources()}
                            <div className="tw-text-lg tw-font-semibold tw-h-full">
                              {isVerdictLoading ? (
                                <Loading />
                              ) : (
                                <ColoredPill
                                  colorParams={{
                                    type: "tier",
                                    value: ampVerdict.tier,
                                  }}
                                  content={ampVerdict.tier}
                                />
                              )}
                            </div>
                            {__IS_COMPONENTS_PORTAL__ && (
                              <button
                                type="button"
                                className="btn btn-xs btn-primary tw-mt-2"
                                onClick={() =>
                                  window.clinicalResults.classifyVariantFromComponent(
                                    tiersSelect.map((tier) => {
                                      return `${tier.name} ${tier.value}`;
                                    }),
                                    ampVerdict.tier
                                  )
                                }
                              >
                                Save as manual classification
                              </button>
                            )}
                            {this.getAMPGeneUsedForAnnotation()}
                          </div>
                        </div>
                      </div>
                      <SampleFindingsTable
                        onEditClick={this.searchModalShow}
                        sampleFindings={ampSampleFindings}
                        rows={[
                          {
                            icon: faDisease,
                            label: "Cancer Type",
                            content: "cancer_type_match",
                            submittedData: cancerType,
                          },
                          {
                            icon: faMicroscope,
                            label: "Tissue Type",
                            content: "tissue_type_match",
                            submittedData: tissueType,
                          },
                          {
                            icon: faWaveSquare,
                            label: "VAF Explain",
                            content: "variant_allele_frequency",
                            submittedData: undefined,
                          },
                          {
                            icon: faUser,
                            label: "Age",
                            content: "age",
                            submittedData: age,
                          },
                          {
                            icon: faGenderless,
                            label: "Sex",
                            content: "sex",
                            submittedData: sexFromAbbreviation,
                          },
                          {
                            icon: faGlobe,
                            label: "Ethnicity",
                            content: "ethnic_frequency",
                            submittedData: ethnicityFromAbbreviation,
                          },
                          {
                            icon: faUsers,
                            label: "Inheritance",
                            content: "inheritance",
                            submittedData: inheritance,
                          },
                        ]}
                      />
                      <ApprovedTherapiesTable
                        approvedTherapies={approvedTherapies}
                      />
                      <Table headers={headers} rows={rows} />
                      {this.displayJaxMessageOnClinical()}
                      {isUserPremium &&
                        this.displayVarsomeClinicalMessageOnVarsome()}
                    </div>
                  ) : (
                    <React.Fragment>{this.getErrors()}</React.Fragment>
                  )}

                  {!isUserPremium &&
                    !isClinical &&
                    !ampClassifications &&
                    !ampVerdict && (
                      <div className="varsome-inner-container tw-text-center">
                        <HubspotBanner
                          bannerId="e7db8e42-5674-4e2b-bf18-8d4739a2588c"
                          altText="The full automated Somatic Variant Classification requires additional data sources"
                          altUrl={`${
                            store.getState().varsomeHost
                          }/varsome-editions/varsome-premium`}
                        />
                      </div>
                    )}
                </React.Fragment>
              }
              title="Somatic Variant Classification"
            />
          </div>
          <Modal
            isOpen={isTermsModalOpen}
            onRequestClose={() => {
              this.setState({
                isTermsModalOpen: false,
              });
            }}
            ContentRender={() => <TermsOfUseAMP />}
          />
          {!__IS_COMPONENTS_PORTAL__ && (
            <SearchModal
              show={isSearchModalOpen}
              handleClose={() => this.searchModalHide()}
            />
          )}
        </div>
      </React.Fragment>
    ) : null;
  }
}

AMP.propTypes = {
  componentTitle: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  hasNext: PropTypes.bool.isRequired,
  genome: PropTypes.shape({}),
  ampVerdict: PropTypes.shape({
    tier: PropTypes.string,
  }),
  ampClassifications: PropTypes.arrayOf(PropTypes.shape({})),
  apiUrl: PropTypes.string.isRequired,
  preferences: PropTypes.shape({}).isRequired,
  ampErrors: PropTypes.arrayOf(PropTypes.shape()),
  version: PropTypes.string,
  isSaphetorUser: PropTypes.bool,
  ampSampleFindings: PropTypes.shape({}),
  acmgAnnotationGene: PropTypes.string,
  acmgCodingImpact: PropTypes.string,
  additionalDatabases: PropTypes.arrayOf(
    PropTypes.shape({
      count: PropTypes.string,
      ids: PropTypes.arrayOf(PropTypes.string),
      source: PropTypes.string,
      version_name: PropTypes.string,
    })
  ),
  approvedTherapies: PropTypes.arrayOf(PropTypes.shape({})),
  geneSymbol: PropTypes.string,
  hgvsP1: PropTypes.string,
  ampThrottlingInfo: PropTypes.shape({
    throttling_msg: PropTypes.string,
  }),
};

AMP.defaultProps = {
  genome: {},
  ampVerdict: undefined,
  ampClassifications: undefined,
  ampErrors: [],
  version: undefined,
  isSaphetorUser: false,
  ampSampleFindings: {},
  acmgAnnotationGene: null,
  acmgCodingImpact: null,
  additionalDatabases: [],
  approvedTherapies: undefined,
  geneSymbol: "",
  hgvsP1: "",
  componentTitle: "",
  ampThrottlingInfo: null,
};

const mapStateToProps = (state) => {
  return {
    isLoading: state.loading,
    hasNext: state.hasNext,
    genome: state.genome,
    ampClassifications: getAmpClassifications(state),
    ampVerdict: getAmpVerdict(state),
    apiUrl: getApiUrl(state),
    preferences: getPreferences(state),
    ampErrors: getAmpErrors(state),
    version: getAmpVersion(state),
    isSaphetorUser: getIfSaphetorUser(state),
    ampSampleFindings: getAmpSampleFindings(state),
    acmgAnnotationGene: getAcmgAnnotationGene(state),
    acmgCodingImpact: getAcmgCodingImpact(state),
    additionalDatabases: getAdditionalDatabases(state),
    varsomeHost: state.varsomeHost,
    approvedTherapies: getApprovedTherapies(state),
    geneSymbol: getGeneSymbol(state),
    hgvsP1: getHgvsP1(state),
    ampThrottlingInfo: getAMPThrottling(state),
  };
};

const mapDispatchToProps = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withErrorGuard(AMP, "AMPComponent", "Somatic Variant Classification"));
